/**
 * PropertyImageScrapeService.js
 *
 * API calls for the property image scraper feature.
 * All HTTP goes through ApiService — never import axios directly in components.
 */

import ApiService from "./ApiService";

class PropertyImageScrapeService {
    /**
     * Fetch the scraper UI config: room types, supported platforms, max images.
     * Called when the scrape modal opens so no values are hardcoded in the component.
     */
    getScrapeConfig() {
        let endPoint = "scrape-config";
        let addditionalHeaderData = {};
        let postData = {};
        let inputData = { endPoint, addditionalHeaderData, postData };
        return ApiService.getCall(inputData);
    }

    /**
     * Update the scrape permission flag on a property.
     * Reuses the existing property update endpoint.
     *
     * @param {string} slug  - Property public slug
     * @param {boolean} confirmed - New permission state
     * @param {string} notes - Optional notes for the permission decision
     */
    updateScrapePermission(slug, confirmed, notes) {
        let endPoint = "property/" + slug;
        let addditionalHeaderData = {};
        let postData = {
            scrape_permission_confirmed: confirmed ? 1 : 0,
            scrape_permission_notes: notes || "",
        };
        let inputData = { endPoint, addditionalHeaderData, postData };
        return ApiService.postCall(inputData);
    }

    /**
     * Dispatch a scrape job for a property.
     *
     * @param {string} slug    - Property public slug
     * @param {string} url     - Listing page URL to scrape
     * @param {object} options - Optional: { room_types, skip_duplicates, ignore_watermarks }
     */
    queueScrape(slug, url, options) {
        let endPoint = "property/" + slug + "/scrape-images";
        let addditionalHeaderData = {};
        let postData = Object.assign({ url }, options || {});
        let inputData = { endPoint, addditionalHeaderData, postData };
        return ApiService.postCall(inputData);
    }

    /**
     * Fetch scraped media_file rows for a property.
     *
     * @param {string} slug          - Property public slug
     * @param {string} reviewStatus  - Optional filter: 'pending' | 'approved' | 'rejected'
     * @param {string} scrapeId      - Optional UUID to limit results to a single scrape session
     */
    getScrapedImages(slug, reviewStatus, scrapeId) {
        let params = [];
        if (reviewStatus) params.push("review_status=" + encodeURIComponent(reviewStatus));
        if (scrapeId) params.push("scrape_id=" + encodeURIComponent(scrapeId));
        let qs = params.length ? "?" + params.join("&") : "";
        let endPoint = "property/" + slug + "/scraped-images" + qs;
        let addditionalHeaderData = {};
        let postData = {};
        let inputData = { endPoint, addditionalHeaderData, postData };
        return ApiService.getCall(inputData);
    }

    /**
     * Poll the cached progress for a running scrape job.
     * Returns {step: int, status: string} where step maps to the Step 2 checklist.
     *
     * @param {string} slug     - Property public slug
     * @param {string} scrapeId - UUID returned by queueScrape
     */
    getScrapeStatus(slug, scrapeId) {
        let endPoint = "property/" + slug + "/scrape-status?scrape_id=" + encodeURIComponent(scrapeId);
        let addditionalHeaderData = {};
        let postData = {};
        let inputData = { endPoint, addditionalHeaderData, postData };
        return ApiService.getCall(inputData);
    }

    /**
     * Approve or reject a single scraped image.
     *
     * @param {string} slug     - Property public slug
     * @param {number} id       - media_file_id
     * @param {string} decision - 'approved' | 'rejected'
     */
    reviewImage(slug, id, decision) {
        let endPoint = "property/" + slug + "/scraped-images/" + id + "/review";
        let addditionalHeaderData = {};
        let postData = { decision };
        let inputData = { endPoint, addditionalHeaderData, postData };
        return ApiService.postCall(inputData);
    }
}

export default new PropertyImageScrapeService();
